import { PerspectiveCamera, OrthographicCamera } from "three";
import type {
  PerspectiveCameraConfig,
  OrthographicCameraConfig,
  CameraConfigType,
  CameraConfig,
} from './type.js';

/**
 * @description 카메라 설정 배열에서 활성 카메라를 찾는 함수
 * @param config - 카메라 설정
 * @returns 활성 카메라 설정
 */
export function findActiveCameraConfig(config: CameraConfig): CameraConfigType | null {
  const activeCamera = config.cameras.find(camera => camera.active);
  return activeCamera || config.cameras[0] || null; // 활성 카메라가 없으면 첫 번째 카메라 반환
}

/**
 * @description 이름으로 카메라 설정을 찾는 함수
 * @param config - 카메라 설정
 * @param name - 카메라 이름
 * @returns 카메라 설정
 */
export function findCameraConfigByName(config: CameraConfig, name: string): CameraConfigType | null {
  return config.cameras.find(camera => camera.name === name) || null;
}

/**
 * @description 카메라 설정 배열에서 모든 카메라 객체를 생성하는 함수
 * @param config - 카메라 설정
 * @param aspectRatio - 화면 비율
 * @returns 카메라 객체들과 이름의 맵
 */
export function createCamerasFromConfig(
  config: CameraConfig,
  aspectRatio: number
): Map<string, PerspectiveCamera | OrthographicCamera> {
  const cameras = new Map<string, PerspectiveCamera | OrthographicCamera>();

  config.cameras.forEach(cameraConfig => {
    const camera = createCameraFromConfig(cameraConfig, aspectRatio);
    cameras.set(cameraConfig.name, camera);
  });

  return cameras;
}

/**
 * @description 카메라 설정에 따라 적절한 카메라 객체를 생성하는 팩토리 함수
 * @param config - 카메라 설정
 * @param aspectRatio - 화면 비율
 * @returns 카메라 객체
 */
export function createCameraFromConfig(
  config: CameraConfigType,
  aspectRatio: number
): PerspectiveCamera | OrthographicCamera {
  switch (config.type) {
    case "PerspectiveCamera":
      return createPerspectiveCamera(config, aspectRatio);
    case "OrthographicCamera":
      return createOrthographicCamera(config);
    default:
      throw new Error(`알 수 없는 카메라 타입: ${(config as { type: string }).type}`);
  }
}

/**
 * @description PerspectiveCamera 생성 함수
 * @param config - PerspectiveCamera 설정
 * @param aspectRatio - 화면 비율
 * @returns PerspectiveCamera 객체
 */
export function createPerspectiveCamera(
  config: PerspectiveCameraConfig,
  aspectRatio: number
): PerspectiveCamera {
  const camera = new PerspectiveCamera(config.fov, aspectRatio, config.near, config.far);

  // 카메라 위치 설정
  camera.position.set(config.position.x, config.position.y, config.position.z);

  // 카메라 회전 설정
  camera.rotation.set(config.rotation.x, config.rotation.y, config.rotation.z);

  // frustumCulled 설정
  if (config.frustumCulled !== undefined) {
    camera.frustumCulled = config.frustumCulled;
  }

  // 투영 행렬 업데이트
  camera.updateProjectionMatrix();

  return camera;
}

/**
 * @description OrthographicCamera 생성 함수
 * @param config - OrthographicCamera 설정
 * @param aspectRatio - 화면 비율
 * @returns OrthographicCamera 객체
 */
export function createOrthographicCamera(
  config: OrthographicCameraConfig,
): OrthographicCamera {
  const camera = new OrthographicCamera(
    config.left,
    config.right,
    config.top,
    config.bottom,
    config.near,
    config.far
  );

  // 카메라 위치 설정
  camera.position.set(config.position.x, config.position.y, config.position.z);

  // 카메라 회전 설정
  camera.rotation.set(config.rotation.x, config.rotation.y, config.rotation.z);

  // frustumCulled 설정
  if (config.frustumCulled !== undefined) {
    camera.frustumCulled = config.frustumCulled;
  }

  // 투영 행렬 업데이트
  camera.updateProjectionMatrix();

  return camera;
}